import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const PICKER_SELECTOR = '[data-venue-zone-picker]';

function parseCoordinate(value) {
    const parsed = Number.parseFloat(value);

    return Number.isFinite(parsed)
        ? parsed
        : null;
}

function parseBoundary(value) {
    try {
        const parsed = JSON.parse(value || '[]');

        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed
            .map((point) => {
                const latitude = parseCoordinate(
                    point?.latitude
                );
                const longitude = parseCoordinate(
                    point?.longitude
                );

                if (
                    latitude === null
                    || longitude === null
                ) {
                    return null;
                }

                return {
                    latitude,
                    longitude,
                };
            })
            .filter(Boolean);
    } catch {
        return [];
    }
}

function initializeZonePicker(picker) {
    if (picker.dataset.venueZoneMapReady === 'true') {
        return;
    }

    const form = picker.closest(
        '[data-venue-zone-form]'
    );
    const mapElement = picker.querySelector(
        '[data-venue-zone-map]'
    );
    const boundaryInput = form?.querySelector(
        '[data-venue-zone-boundary]'
    );
    const pointCount = picker.querySelector(
        '[data-venue-zone-point-count]'
    );
    const status = picker.querySelector(
        '[data-venue-zone-status]'
    );
    const undoButton = picker.querySelector(
        '[data-venue-zone-undo]'
    );
    const clearButton = picker.querySelector(
        '[data-venue-zone-clear]'
    );

    if (
        !form
        || !mapElement
        || !boundaryInput
    ) {
        return;
    }

    picker.dataset.venueZoneMapReady = 'true';

    const centerLatitude = parseCoordinate(
        picker.dataset.centerLatitude
    );
    const centerLongitude = parseCoordinate(
        picker.dataset.centerLongitude
    );

    let points = parseBoundary(
        boundaryInput.value
    );
    let polygon = null;
    let pointMarkers = [];

    const map = L.map(
        mapElement,
        {
            zoomControl: true,
            scrollWheelZoom: false,
        }
    ).setView(
        [
            centerLatitude ?? 0,
            centerLongitude ?? 0,
        ],
        centerLatitude !== null
            && centerLongitude !== null
            ? 17
            : 2
    );

    L.tileLayer(
        'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        {
            maxZoom: 20,
            attribution:
                '&copy; OpenStreetMap contributors',
        }
    ).addTo(map);

    const updateStatus = () => {
        const count = points.length;

        if (pointCount) {
            pointCount.textContent =
                `${count} ${count === 1 ? 'point' : 'points'}`;
        }

        undoButton?.toggleAttribute(
            'disabled',
            count === 0
        );
        clearButton?.toggleAttribute(
            'disabled',
            count === 0
        );

        if (!status) {
            return;
        }

        if (count === 0) {
            status.textContent =
                'Click around the edge of the zone to begin drawing.';
        } else if (count < 3) {
            status.textContent =
                `Add ${3 - count} more ${3 - count === 1 ? 'point' : 'points'} to form a zone.`;
        } else {
            status.textContent =
                'Zone boundary is valid. Add more points for detail, or save the zone.';
        }
    };

    const syncInput = () => {
        boundaryInput.value = JSON.stringify(
            points.map((point) => ({
                latitude: Number(
                    point.latitude.toFixed(7)
                ),
                longitude: Number(
                    point.longitude.toFixed(7)
                ),
            }))
        );
    };

    const clearPointMarkers = () => {
        pointMarkers.forEach((marker) => {
            map.removeLayer(marker);
        });

        pointMarkers = [];
    };

    const renderBoundary = (
        fit = false
    ) => {
        if (polygon) {
            map.removeLayer(polygon);
            polygon = null;
        }

        clearPointMarkers();

        points.forEach((point, index) => {
            const marker = L.circleMarker(
                [
                    point.latitude,
                    point.longitude,
                ],
                {
                    radius: 6,
                    color: '#ffffff',
                    weight: 2,
                    fillColor: '#0f766e',
                    fillOpacity: 1,
                }
            )
                .addTo(map)
                .bindTooltip(
                    `Point ${index + 1}`,
                    {
                        direction: 'top',
                    }
                );

            pointMarkers.push(marker);
        });

        if (points.length >= 2) {
            polygon = L.polygon(
                points.map((point) => [
                    point.latitude,
                    point.longitude,
                ]),
                {
                    color: '#0f766e',
                    weight: 3,
                    fillColor: '#14b8a6',
                    fillOpacity: points.length >= 3
                        ? 0.2
                        : 0.05,
                }
            ).addTo(map);
        }

        syncInput();
        updateStatus();

        if (
            fit
            && points.length > 0
        ) {
            const bounds = L.latLngBounds(
                points.map((point) => [
                    point.latitude,
                    point.longitude,
                ])
            );

            if (bounds.isValid()) {
                map.fitBounds(
                    bounds,
                    {
                        padding: [50, 50],
                        maxZoom: 18,
                        animate: false,
                    }
                );
            }
        }
    };

    map.on(
        'click',
        (event) => {
            points.push({
                latitude: event.latlng.lat,
                longitude: event.latlng.lng,
            });

            renderBoundary();
        }
    );

    undoButton?.addEventListener(
        'click',
        () => {
            if (points.length === 0) {
                return;
            }

            points.pop();
            renderBoundary();
        }
    );

    clearButton?.addEventListener(
        'click',
        () => {
            points = [];
            renderBoundary();
        }
    );

    form.addEventListener(
        'submit',
        (event) => {
            if (points.length >= 3) {
                syncInput();
                return;
            }

            event.preventDefault();

            if (status) {
                status.textContent =
                    'Draw at least three points before saving this zone.';
            }

            mapElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    );

    renderBoundary(
        points.length > 0
    );

    window.setTimeout(
        () => map.invalidateSize(),
        100
    );
}

function initializeZonePickers() {
    document
        .querySelectorAll(PICKER_SELECTOR)
        .forEach(initializeZonePicker);
}

if (document.readyState === 'loading') {
    document.addEventListener(
        'DOMContentLoaded',
        initializeZonePickers,
        {
            once: true,
        }
    );
} else {
    initializeZonePickers();
}

document.addEventListener(
    'livewire:navigated',
    initializeZonePickers
);
